import React, { useState } from 'react';
import type {
  AhqSubmission,
  CandidateReputationClarificationPatch,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import {
  Layout,
  Gutter,
  Grid,
  GridRow,
  GridColumn,
  Card,
  CardSection,
  EmptyLayout,
  Flashy,
  SectionHeader,
  CondensedMedium,
} from '@axiom/ui';

import NoResults from '../../public/svg/no-results-binoculars.svg';
import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';

import { OpportunitySubmissionUrl } from './OpportunitySubmissionUrl';
import { OpportunitySubmissionCandidateList } from './OpportunitySubmissionCandidateList';
import { OpportunitySubmissionSelectedCandidate } from './OpportunitySubmissionSelectedCandidate';
import { OpportunitySubmissionUtil } from './opportunity-submission-util';

export type OpportunitySubmissionContentProps = {
  submission: AhqSubmission;
  candidates: OpportunityCandidate[];
  candidateReputationClarificationPatchCall: CandidateReputationClarificationPatch[];
  opportunity: Opportunity;
  readOnly?: boolean;
  initialCandidateId?: string;
  name?: string;
};

export const OpportunitySubmissionContent = ({
  submission,
  candidates,
  candidateReputationClarificationPatchCall,
  opportunity,
  readOnly = false,
  initialCandidateId,
  name,
}: OpportunitySubmissionContentProps) => {
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
    () => {
      if (
        initialCandidateId &&
        candidates.some(c => c.id === initialCandidateId)
      ) {
        return initialCandidateId;
      }
      return candidates.length > 0 ? candidates[0]?.id || null : null;
    }
  );

  const selectedCandidate = candidates.find(c => c.id === selectedCandidateId);

  const handleSubmissionDescriptionSave = async (
    candidateId: string,
    description: string
  ) => {
    await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
      candidates: [{ candidateId, submissionDescription: description }],
      newSubmissionType: submission.newSubmissionType,
    });
    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId: submission.submissionId,
      opportunityId: opportunity.id,
      candidateId,
    });
  };

  const emptySubmission = candidates.length === 0 || !selectedCandidate;

  if (emptySubmission) {
    return (
      <Card name={name}>
        <CardSection>
          <Grid>
            <GridRow>
              <GridColumn width={4} />
              <GridColumn width={4}>
                <EmptyLayout
                  graphic={<NoResults width="117px" height="117px" />}
                  name="NO_CANDIDATES"
                >
                  <Layout stretched direction="vertical">
                    <SectionHeader>No Submitted Talent</SectionHeader>
                    There are no talent submitted to this opportunity yet.
                    Please submit some to start making a submission.
                  </Layout>
                </EmptyLayout>
              </GridColumn>
              <GridColumn width={4} />
            </GridRow>
          </Grid>
        </CardSection>
      </Card>
    );
  }

  return (
    <div data-test={name}>
      <Grid>
        <GridRow>
          <GridColumn largeScreenWidth={4} stretched>
            <Card stretched>
              <CardSection background="blue">
                <SectionHeader>
                  <Flashy color="textReversed">Manage Submission</Flashy>
                </SectionHeader>
                <Gutter bottom="8px" />
                <CondensedMedium>
                  <Flashy color="textReversed">
                    Curated talent will appear in the order you see here. Drag
                    and drop to reorder talent as needed. To add or remove
                    talent, go back to the Talent tab.
                  </Flashy>
                </CondensedMedium>
              </CardSection>
            </Card>
          </GridColumn>
          <GridColumn largeScreenWidth={8}>
            <Card>
              <CardSection>
                <OpportunitySubmissionUrl
                  name="OPPORTUNITY_SUBMISSION_URL"
                  submission={submission}
                  candidates={candidates}
                  opportunity={opportunity}
                  readOnly={readOnly}
                />
              </CardSection>
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>

      <Gutter top="16px" />

      <Grid>
        <GridRow>
          <GridColumn width={4}>
            <OpportunitySubmissionCandidateList
              name="OPPORTUNITY_SUBMISSION_CANDIDATE_LIST"
              submission={submission}
              candidates={candidates}
              selectedCandidateId={selectedCandidateId || ''}
              onSelectedCandidateChange={setSelectedCandidateId}
              readOnly={readOnly}
              opportunity={opportunity}
            />
          </GridColumn>

          <GridColumn width={8}>
            <OpportunitySubmissionSelectedCandidate
              key={selectedCandidate.id}
              name="OPPORTUNITY_SUBMISSION_SELECTED_CANDIDATE"
              candidate={selectedCandidate}
              initialCandidate={selectedCandidate}
              submission={submission}
              candidates={candidates}
              candidateReputationClarificationPatchCall={
                candidateReputationClarificationPatchCall
              }
              opportunity={opportunity}
              readOnly={readOnly}
              onSubmissionDescriptionSave={(candidateId, description) => {
                void handleSubmissionDescriptionSave(candidateId, description);
              }}
            />
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
};
